import {
  Flex,
  type FlexProps,
  ReportSection,
  ReportSectionItemContent,
} from '@/components';
import type { LedgerKsInspectionEvaluationSectionProps } from '@/features/mold/models/ledger';
import {
  DiscriminatedInspectionEvaluationModel,
  ExistenceOrAbsenceLabelModifier,
} from '@/features/project/projects/_models/project';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BORDER,
  formatOwnershipRatioFraction,
  formatOwnershipRatioPartsOfFraction,
  formatOwnershipRatioPercentage,
} from '../../../utils';

export const KsInspectionEvaluationSectionBefore2025: FC<
  LedgerKsInspectionEvaluationSectionProps
> = ({ project }) => {
  const { t } = useTranslation();

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(project);

  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }

  const { inspectionEvaluation } = parsed.data;

  return (
    <ReportSection
      title={t('inspection-evaluation')}
      items={[
        {
          subtitle: t('self-inspection-by-fund-jurisdiction'),
          item: (
            <Flex style={{ height: '100%' }}>
              <InspectionEvaluationContentItem
                label={t('ownership-ratio')}
                value={
                  inspectionEvaluation.ownershipRatio?.operationType === 'auto'
                    ? formatOwnershipRatioFraction(
                        inspectionEvaluation.ownershipRatio.ratioNumerator,
                        inspectionEvaluation.ownershipRatio.ratioDenominator,
                      )
                    : formatOwnershipRatioPercentage(
                        inspectionEvaluation.ownershipRatio?.manualRatio,
                      )
                }
                isLast
              />
              {inspectionEvaluation.ownershipRatio?.operationType ===
                'auto' && (
                <>
                  <InspectionEvaluationContentItem
                    label={t('ownership-ratio-numerator-before-2025')}
                    value={formatOwnershipRatioPartsOfFraction(
                      inspectionEvaluation.ownershipRatio.ratioNumerator,
                    )}
                    wrapperStyle={{
                      height: '100%',
                      borderLeft: LEDGER_BORDER,
                    }}
                    isLast
                  />
                  <InspectionEvaluationContentItem
                    label={t('ownership-ratio-denominator-before-2025')}
                    value={formatOwnershipRatioPartsOfFraction(
                      inspectionEvaluation.ownershipRatio.ratioDenominator,
                    )}
                    wrapperStyle={{
                      height: '100%',
                      borderLeft: LEDGER_BORDER,
                    }}
                    isLast
                  />
                </>
              )}
            </Flex>
          ),
        },
        {
          subtitle: t('ownership-ratio'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t('basis-calculation-formula')}
                value={
                  inspectionEvaluation.ownershipRatio?.basis?.calculationFormula
                }
              />
              <InspectionEvaluationContentItem
                label={t('contents-of-each-term')}
                value={inspectionEvaluation.ownershipRatio?.basis?.contents}
              />
              <InspectionEvaluationContentItem
                label={t('concept-of-basis-calculation-formula')}
                value={
                  inspectionEvaluation.ownershipRatio?.conceptOfProjectProspect
                    ?.calculationFormula
                }
              />
              <InspectionEvaluationContentItem
                label={t('concept-of-contents-of-each-term-before-2025')}
                value={
                  inspectionEvaluation.ownershipRatio?.conceptOfProjectProspect
                    ?.contents
                }
              />
              <InspectionEvaluationContentItem
                label={t(
                  'basis-for-estimating-indicators-for-project-prospect',
                )}
                value={
                  inspectionEvaluation.ownershipRatio?.conceptOfProjectProspect
                    ?.basisForCalculationOfIndicators
                }
              />
              <InspectionEvaluationContentItem
                label={t(
                  'indicators-for-project-prospect-and-recent-performance',
                )}
                value={
                  inspectionEvaluation.ownershipRatio?.conceptOfProjectProspect
                    ?.moreRecentResults
                }
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('low-used-fund-investigation'),
          item: (
            <Flex vertical>
              <Flex>
                <InspectionEvaluationContentItem
                  label={t('has-ceased-operations')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasCeasedOperations === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasCeasedOperations
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                />
                <InspectionEvaluationContentItem
                  label={t('has-no-operation-fund-detected')}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasNoResultRecent === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasNoResultRecent
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                />
                <InspectionEvaluationContentItem
                  label={t('has-fund-that-lost-purpose')}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasFundThatLostPurpose === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasFundThatLostPurpose
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                />
                <InspectionEvaluationContentItem
                  label={t('has-exceed-1-significantly')}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasExceedOneSignificantly === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasExceedOneSignificantly
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                />
                <InspectionEvaluationContentItem
                  label={t('has-deemed-unlikely-to-be-used')}
                  wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                  value={
                    typeof inspectionEvaluation.lowUsedFundInvestigation
                      ?.hasDeemedUnlikelyToBeUsed === 'boolean'
                      ? inspectionEvaluation.lowUsedFundInvestigation
                          .hasDeemedUnlikelyToBeUsed
                        ? ExistenceOrAbsenceLabelModifier.true
                        : ExistenceOrAbsenceLabelModifier.false
                      : null
                  }
                />
              </Flex>
              <InspectionEvaluationContentItem
                label={t(
                  'reason-for-selecting-absense-when-ownership-ratio-exceeds-1',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.reasonsForBeingFalseWhileExceedingOne
                }
              />
              <InspectionEvaluationContentItem
                label={t(
                  'consideration-where-the-fund-are-unlikely-to-be-used',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.resultsOfInvestigations
                }
              />
              <InspectionEvaluationContentItem
                label={t(
                  'reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
                )}
                value={
                  inspectionEvaluation.lowUsedFundInvestigation
                    ?.reasonsForLeaving
                }
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('appropriateness-inspection'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t('reason-why-bulk-delivering-was-necessary')}
                value={
                  inspectionEvaluation.appropriatenessInspection
                    ?.reasonsWhyBulkDeliveringWasNecessary
                }
              />
              <InspectionEvaluationContentItem
                label={t(
                  'approach-in-determining-the-timing-and-amount-of-additions',
                )}
                value={
                  inspectionEvaluation.appropriatenessInspection
                    ?.approachInDeterminingTheTimingAndAmountOfAdditions
                }
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('implementation-status-of-inspection'),
          isLongText: true,
          item: (
            <InspectionEvaluationContentItem
              isSpaceLabel
              value={inspectionEvaluation.implementationStatusOfInspection}
              isLast
            />
          ),
        },
        {
          subtitle: t('eligibility-inspection'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t(
                  'eligibility-inspection-method-and-reasons-for-selection-before-2025',
                )}
                value={
                  inspectionEvaluation.eligibilityInspection
                    ?.methodAndReasonsForSelection
                }
              />
              <InspectionEvaluationContentItem
                label={t('eligibility-inspection-results-before-2025')}
                value={inspectionEvaluation.eligibilityInspection?.results}
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('inspection-and-improvement-by-fund-jurisdiction'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t('inspection-result')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.inspectionResult
                }
              />
              <InspectionEvaluationContentItem
                label={t('evaluation')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.evaluation
                }
              />
              <InspectionEvaluationContentItem
                label={t('direction-of-improvement')}
                value={
                  inspectionEvaluation
                    .inspectionAndImprovementByAdministrativeJurisdiction
                    ?.directionOfImprovement
                }
                isLast
              />
            </Flex>
          ),
        },
        {
          subtitle: t('inspection-by-other-than-fund-jurisdiction'),
          item: (
            <Flex vertical>
              <InspectionEvaluationContentItem
                label={t('findings-of-external-experts')}
                value={inspectionEvaluation.findingsOfExternalExperts}
              />
              <InspectionEvaluationContentItem
                label={t(
                  'process-leading-to-the-findings-of-review-promotion-team',
                )}
                value={
                  inspectionEvaluation.processLeadingToTheFindingsOfReviewPromotionTeam
                }
              />
              <InspectionEvaluationContentItem
                label={t('improvement-point-based-on-the-findings')}
                value={inspectionEvaluation.improvementPointBasedOnTheFindings}
              />
              <InspectionEvaluationContentItem
                label={t('overview-of-reviews-carried-out-in-the-past')}
                value={
                  inspectionEvaluation.overviewOfReviewsCarriedOutInThePast
                }
                isLast
              />
            </Flex>
          ),
        },
      ]}
    />
  );
};

const InspectionEvaluationContentItem: FC<{
  label?: string;
  isSpaceLabel?: boolean;
  value: ReactNode;
  wrapperStyle?: FlexProps['style'];
  isLast?: boolean;
}> = ({ label, isSpaceLabel, value, isLast, wrapperStyle }) => {
  const inspectionEvaluationLabelStyle = {
    width: 150,
    minWidth: 150,
  };
  return (
    <ReportSectionItemContent
      label={label}
      isSpaceLabel={isSpaceLabel}
      value={value}
      labelStyle={inspectionEvaluationLabelStyle}
      wrapperStyle={{
        borderBottom: isLast ? undefined : LEDGER_BORDER,
        height: 'auto',
        ...wrapperStyle,
      }}
    />
  );
};
